import { toMillis } from "./firestoreTime";

/**
 * Lap crossings for one result, oldest first.
 *
 * Times are read through toMillis, not `new Date(...)`, because the two timing
 * sources store them differently: a MYLAPS passing arrives as an ISO string,
 * while a manually entered lap is a Firestore Timestamp. `new Date(Timestamp)`
 * yields Invalid Date, so every lap of a manually timed event was dropped by
 * the isFinite filter below — the venue board showed no crossings, a blank lap
 * clock, and a leaderboard ordered by BIB instead of by race position.
 */
export const loopEntriesOf = (result = {}) =>
  Object.values(result.checkpoints || {})
    .flatMap((checkpoint) =>
      checkpoint && typeof checkpoint === "object"
        ? Object.entries(checkpoint)
            .filter(([key, value]) => /^loop\d+$/.test(key) && value?.time)
            .map(([key, value]) => ({
              number: Number(key.slice(4)),
              time: value.time,
              timeMs: toMillis(value.time),
              ...value,
            }))
        : []
    )
    .filter((entry) => Number.isFinite(entry.timeMs))
    .sort((a, b) => a.timeMs - b.timeMs);

export const latestLoopOf = (result = {}) => {
  const entries = loopEntriesOf(result);
  return entries.at(-1) || null;
};

export const completedLoopsOf = (result = {}) =>
  Math.max(Number(result.completedLoops) || 0, latestLoopOf(result)?.number || 0);

export const loopClockOf = (result = {}) => {
  const latest = latestLoopOf(result);
  if (!latest) return "--:--:--";
  return new Date(latest.timeMs).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export const rankLoopResults = (results = []) => {
  const eligible = results.filter(
    (result) => !["DNF", "DNS", "DSQ"].includes(String(result.status || "").toUpperCase())
  );
  const ranked = [...eligible].sort((a, b) => {
    const loopDifference = completedLoopsOf(b) - completedLoopsOf(a);
    if (loopDifference) return loopDifference;
    const aTime = latestLoopOf(a)?.timeMs ?? Number.POSITIVE_INFINITY;
    const bTime = latestLoopOf(b)?.timeMs ?? Number.POSITIVE_INFINITY;
    if (aTime !== bTime) return aTime - bTime;
    return String(a.bib || "").localeCompare(String(b.bib || ""), undefined, { numeric: true });
  });

  const genderCounts = new Map();
  return ranked.map((result, index) => {
    const gender = String(result.gender || "").toLowerCase();
    const genderKey = gender === "m" || gender === "male" ? "male" : gender === "f" || gender === "female" ? "female" : "other";
    const genderRank = (genderCounts.get(genderKey) || 0) + 1;
    genderCounts.set(genderKey, genderRank);
    return { ...result, overallRank: index + 1, genderRank };
  });
};

export const rankRelayResults = (results = []) => {
  const teams = new Map();
  results.forEach((result) => {
    const teamId = String(result.teamId || "").trim();
    if (!teamId) return;
    if (!teams.has(teamId)) {
      teams.set(teamId, {
        id: `relay-${teamId}`,
        bib: "",
        teamId,
        teamName: result.teamName || teamId,
        name: result.teamName || teamId,
        categoryId: result.categoryId,
        race: result.race || result.categoryId,
        isTeam: true,
        members: [],
        _entries: [],
      });
    }
    const team = teams.get(teamId);
    if (result.teamName) team.teamName = team.name = result.teamName;
    team.members.push({
      bib: result.bib,
      name: result.name,
      gender: result.gender,
      relayMemberNo: result.relayMemberNo,
    });
    loopEntriesOf(result).forEach((entry) => team._entries.push({ ...entry, runnerBib: entry.runnerBib || result.bib, runnerName: entry.runnerName || result.name }));
    const status = String(result.status || "").toUpperCase();
    if (["DNF", "DNS", "DSQ"].includes(status)) team.status = status;
  });

  const teamRows = [...teams.values()].map((team) => {
    const members = [...team.members].sort((a, b) => {
      const memberOrder = (Number(a.relayMemberNo) || Number.POSITIVE_INFINITY)
        - (Number(b.relayMemberNo) || Number.POSITIVE_INFINITY);
      return memberOrder || String(a.bib || "").localeCompare(String(b.bib || ""), undefined, { numeric: true });
    });
    const leader = members[0];
    const entries = team._entries
      .sort((a, b) => a.timeMs - b.timeMs)
      .map((entry, index) => ({ ...entry, number: index + 1, loopNumber: index + 1 }));
    const last = entries.at(-1);
    // Name the row after the runner currently on course, not the team.
    //
    // The board showed the team name beside the running member's BIB, so a team
    // registered as "MAS BAY" appeared next to BIB 1533 with no hint that Tyo
    // was the one running. Teams that registered under their own member's name
    // hid the problem — their team name happened to read like a person.
    //
    // The last accepted crossing identifies the current leg: each lap entry
    // carries the BIB that recorded it. Falls back to the team name before the
    // first crossing, when nobody is on course yet.
    const lastRunnerResult = results.find(
      (result) => String(result.bib) === String(last?.runnerBib)
    );
    const currentRunner = last?.runnerBib
      ? members.find((member) => String(member.bib) === String(last.runnerBib))
      : null;
    const currentName = last?.runnerName || currentRunner?.name || team.teamName;
    return {
      ...team,
      name: currentName,
      // Keep the team identity available for anything that wants to show it.
      teamName: team.teamName,
      currentRunnerBib: currentRunner?.bib || last?.runnerBib || leader?.bib || "",
      bib: currentRunner?.bib || last?.runnerBib || leader?.bib || "",
      memberBibs: members.map((member) => String(member.bib || "")).filter(Boolean),
      members,
      _entries: undefined,
      completedLoops: entries.length,
      checkpoints: {
        relay: Object.fromEntries(entries.map((entry) => [`loop${entry.number}`, entry])),
      },
      finishMatTime: last?.time || null,
      lastCheckpoint: last ? { id: "relay", time: last.time, loopNumber: last.number, runnerBib: last.runnerBib } : null,
      // Carry finishDuration alongside finishTime: on a manually timed event
      // the elapsed time lives in the former, and a row that forwarded only
      // finishTime handed the display a Firestore Timestamp to render.
      finishTime: lastRunnerResult?.finishTime || null,
      finishDuration: lastRunnerResult?.finishDuration || null,
      gender: "",
      nationality: "N/A",
    };
  });
  return rankLoopResults(teamRows);
};
